import useSWR, { mutate } from 'swr';
import services from 'services';
import utils from 'utils';
import { UserCsvSep, UserDate, UserDecimalSep } from 'models/user';
import { TableColumn } from 'components/Table';

/**
 * Custom hook to fetch errors using SWR for caching
 * @param {number | null} idCompany - The company ID to fetch errors for
 * @return {object} - { data, error, isLoading, refresh }
 */
export const useErrors = (idCompany: number | null) => {
  const key = idCompany !== null ? `/errors/${idCompany}` : null;

  const { data, error } = useSWR(
    key,
    async () => {
      const response = await services.getErrors(idCompany);
      return response.data;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const isLoading = !data && !error && key !== null;

  // Function to manually refresh the data
  const refresh = () => {
    if (key) {
      mutate(key);
    }
  };

  // Function to clear cache for a specific company
  const clearCache = () => {
    if (key) {
      mutate(key, undefined, false);
    }
  };

  return {
    data: data || [],
    error,
    isLoading,
    refresh,
    clearCache,
  };
};

// Function to manually invalidate cache from anywhere
export const invalidateErrorsCache = (idCompany: number | null) => {
  if (idCompany !== null) {
    mutate(`/errors/${idCompany}`);
  }
};

export function errorColums(t: (label: string) => string, userPreference: { base64Image: string | undefined; dateFormat: UserDate; decimalSep: UserDecimalSep; notifyMail: 0 | 1 | undefined; csvSep: UserCsvSep; timeZone: string; openNextDocument: boolean | undefined; openInvoiceLogAutomatically: boolean | undefined; }): TableColumn[] {
  return [
    {
      Header: t('errorType'),
      accessor: 'errorType',
      filterType: 'select',
    },
    {
      Header: t('protocol'),
      accessor: 'protocol',
      filterType: 'free',
    },
    {
      Header: t('dateIn'),
      accessor: 'dateIn',
      filterType: 'date',
      filter: utils.date.epochFilterFunction(userPreference.timeZone),
      Cell: ({ value }: { value: number; }) => utils.date.convertDate(value, userPreference.timeZone ?? 'UTC'),
      dateFormat: userPreference.dateFormat,
    },
    {
      Header: t('dateTry'),
      accessor: 'dateTry',
      filterType: 'date',
      filter: utils.date.epochFilterFunction(userPreference.timeZone),
      Cell: ({ value }: { value: number; }) => utils.date.convertDate(value, userPreference.timeZone ?? 'UTC'),
      dateFormat: userPreference.dateFormat,
    },
    {
      Header: t('errorMessage'),
      accessor: 'errorMessage',
      filterType: 'free',
    },
    {
      Header: t('company'),
      accessor: 'company',
      filterType: 'select',
    },
    {
      Header: t('sourceChannel'),
      accessor: 'sourceChannel',
      filterType: 'free',
    },
    {
      Header: t('formAdr'),
      accessor: 'formAdr',
      filterType: 'free',
    },
    {
      Header: t('toAdr'),
      accessor: 'toAdr',
      filterType: 'free',
    },
    {
      Header: t('subject'),
      accessor: 'subject',
      filterType: 'free',
    },
    {
      Header: t('docName'),
      accessor: 'docName',
      filterType: 'free',
    },
    {
      Header: t('originalProtIn'),
      accessor: 'originalProtIn',
      filterType: 'free',
    },
    {
      Header: t('originalProtocol'),
      accessor: 'originalProtocol',
      filterType: 'free',
    },
    {
      Header: t('documentType'),
      accessor: 'documentType',
      filterType: 'free',
    },
  ];
}

